import uib from 'angular-ui-bootstrap';

export default angular.module('eventix.common.services.modalCRUD',[uib])
/**
 * @ngdoc directive
 * @name eventix.common.directives:localController
 * @restrict attribute
 * @description
 * Load a controller with view outside of the uiRouter paradigm. It's basically an
 * extension of `ngController` and `ngInclude` that supports passing custom
 * dependency injections.
 *
 * This is used by {@link eventix.common.services.modalCRUD} to load a controller that requires the injection of
 * resolved resources.
 *
 * @param {String} localController Name of the controller, is evaluated on scope
 * @param {String} controllerAs Bind controller to scope as (...)
 * @param {Object} locals Local dependencies as `{key: value}` (must be resolved already)
 * @example
 * ```
 * <div local-controller="'MainController'"
 *      controller-as="'vm'"
 *      locals="{ user: $ctrl.user, isAdmin: false }"
 *      ng-include="'/path/to/template.html'"></div>
 * ```
 */
    .directive('localController', function($controller) {
        return {
            restrict: 'A',
            scope: false,
            link: function(scope, el, attr) {
                var locals = scope.$eval(attr.locals);
                var controller = scope.$eval(attr.localController);
                var controllerAs = scope.$eval(attr.controllerAs);
                angular.extend(locals, {$scope: scope});
                controller = $controller(controller, locals);
                if(controllerAs)
                    scope[controllerAs] = controller;
            }
        };
    })
/**
 * @ngdoc service
 * @name eventix.common.services.modalCRUD
 * @kind function
 * @description
 * Service to load another controller inside a modal, complete with resolves.
 *
 * @param {String} controller Name of the controller. Controller can request a `$modal` injection to manipulate
 * `resolve` & `reject` behaviour or adjust translation strings dynamically
 * @param {String} templateUrl Path to template for controller
 * @param {Object} [resolve] Dependencies that are to be injected when they resolve.
 * It's an object that mimicks uiRouter's resolve paradigm `{name: fn}`
 * @param {Object} [translations] Custom (ngTranslate) strings for `title`, `cancel` and `close` button
 * @param {String} [controllerAs=vm] Bind controller to scope as (...)
 * @param {String} [size=md] Size of modal
 * @returns {Promise} Resolves when the modal is closed
 */
    .factory('modalCRUD', function($uibModal, $compile, $q) {
        return function(controller, templateUrl, resolve = {}, translations = { close: 'common.action.close' }, controllerAs = 'vm', size = 'md') {
            var modal = $uibModal.open({
                controller: /*@ngInject*/function($scope, ctrl, locals, translations) {
                    _.assign($scope, ctrl);
                    $scope.modal = {
                        busy: false,
                        resolve: $scope.$close,
                        reject: $scope.$dismiss,
                        translations: translations
                    };
                    locals.$modal = $scope.modal;
                    $scope.locals = locals;
                    $scope.translations = translations;
                },
                size: size,
                resolve: {
                    ctrl: function() {
                        return {
                            controller: controller,
                            controllerAs: controllerAs,
                            resolve: resolve,
                            templateUrl: templateUrl
                        };
                    },
                    translations: () => translations,
                    locals: function() {
                        var promises = _.map(resolve, (value,key) => {
                            return $q.resolve(value()).then(resolved => [key, resolved]);
                        });

                        return $q.all(promises).then( resolved => {
                            return $q.resolve( _.fromPairs(resolved) );
                        });
                    }
                },
                template: `<div class="modal-content">
    <div class="modal-header" ng-if="translations.title">
        <h3 class="modal-title" translate>{{  translations.title }}</h3>
    </div>
    <div class="modal-body" id="modalCRUD">
    </div>
    <div class="modal-footer">
        <button class="btn btn-default" ng-disabled="modal.busy" ng-click="modal.reject()" translate="{{translations.cancel}}" ng-if="translations.cancel"></button>
        <button class="btn btn-info" ng-click="modal.resolve()" ng-disabled="modal.busy">
            <i ng-show="modal.busy" class="fa fa-spin fa-spinner"></i>
            <translate>{{translations.close}}</translate></button>
    </div>
</div>`
            });
            modal.rendered.then(() => {
                var $div = $('<div local-controller="controller" ng-include="templateUrl" locals="locals" controller-as="controllerAs"></div>');
                var scope = angular.element('#modalCRUD').scope();
                angular.element('#modalCRUD').html($div);
                $compile($div)(scope);
            });

            return modal.result;
        };
    }).name;
